import { ChevronRightIcon } from '@heroicons/react/20/solid';
import { FC, useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useContextSelector } from 'use-context-selector';

import { cacheSelector } from '@/core/store/cache/cache-state';
import { CacheContext } from '@/core/store/cache/CacheProvider';
import { authSelector } from '@/core/store/global/app-states';
import { AppContext } from '@/core/store/global/AppProvider';
import ColorSelector from '@/ux-legacy/components/common/bentoboard/Options/ColorSelector';
import { LoadingCircleIcon } from '@/ux-legacy/uikit/iconset/iconset-index';

import SelectOption from './SelectOption';

type WaterMarkDataProps = {
  getWatermark: any;
  savingData: (data: any) => void;
  showBackground?: boolean;
};

const WaterMarkData: FC<WaterMarkDataProps> = ({ getWatermark, savingData, showBackground }) => {
  const position = [
    { id: 1, value: 'top_left', name: 'Top Left' },
    { id: 2, value: 'top_right', name: 'Top Right' },
    { id: 3, value: 'bottom_left', name: 'Bottom Left' },
    { id: 4, value: 'bottom_right', name: 'Bottom Right' },
    { id: 5, value: 'center_top', name: 'Center Top' },
    { id: 6, value: 'center_bottom', name: 'Center Bottom' },
    { id: 7, value: 'center_left', name: 'Center Left' },
    { id: 8, value: 'center_right', name: 'Center Right' },
    { id: 9, value: 'center', name: 'Center' },
  ];

  const [isUploading, setIsUploading] = useState(false);
  const authentication = useContextSelector(AppContext, authSelector);
  const [exisitingData, setExisitingData] = useState(null);
  const [showBgColorPicker, setShowBgColorPicker] = useState<boolean>(false);
  const [showColorPicker, setShowColorPicker] = useState<boolean>(false);
  const cache = useContextSelector(CacheContext, cacheSelector);
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm();
  const colorPickerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (cache['waterMarkLoading'] !== undefined) {
      setIsUploading(cache['waterMarkLoading']);
    }
  }, [cache['waterMarkLoading']]);

  useEffect(() => {
    if (getWatermark !== undefined) {
      setExisitingData(getWatermark);
      // console.log(getWatermark);

      const selectedPosotion = position.find((item) => item?.value === getWatermark?.position);
      setValue('position', selectedPosotion || position[0]);
      if (getWatermark?.text?.opacity) {
        setValue('opacity', getWatermark?.text?.opacity || 0);
      }

      if (getWatermark?.text?.value) {
        setValue('value', getWatermark?.text?.value);
      }

      if (getWatermark?.text?.fontColor) {
        setValue('color', getWatermark?.text?.fontColor);
      }

      if (getWatermark?.text?.background) {
        setValue('background', getWatermark?.text?.background);
      }
    } else {
      setExisitingData(null);
      setValue('position', position[0]);
    }
  }, [getWatermark]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent | TouchEvent) => {
      if (colorPickerRef.current && !colorPickerRef.current.contains(event.target as Node)) {
        setShowBgColorPicker(false);
        setShowColorPicker(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [colorPickerRef]);

  const onSubmit = (data: any) => {
    savingData(data);
  };

  // const errorMessage = (error: string) => {
  //   dispatch(
  //     setNotificationAction({
  //       notification: {
  //         show: true,
  //         variant: 'error',
  //         message: error,
  //       },
  //     }),
  //   );
  // };

  // const successMessage = (message: string) => {
  //   dispatch(
  //     setNotificationAction({
  //       notification: {
  //         show: true,
  //         variant: 'success',
  //         message: message,
  //       },
  //     }),
  //   );
  // };

  return (
    <>
      <div className="md:flex justify-center w-full ">
        <div className="p-4 gap-4 overflow-auto h-auto md:w-1/2">
          <div className="mb-5 w-full">
            <div className="font-bold text-black">Position</div>
            <SelectOption
              selected={watch('position')}
              ListData={position}
              onChange={(data) => {
                setValue('position', data);
              }}
            />
          </div>
          <div className="mb-5 w-full">
            <div className="font-bold text-black">Text</div>
            <textarea
              id="description"
              rows={4}
              autoComplete="off"
              className="border border-gray-300 mt-2 bg-gray-200 p-2 py-3 max-h-[100px] min-h-[100px] rounded-md text-gray-900 w-full text-[15px] focus:ring-2 focus:ring-inset focus:outline-none focus:ring-black"
              placeholder="Watermark text"
              value={watch('value')}
              maxLength={15}
              // onChange={(e) => {
              //   const value = e.target.value;
              //   if (value.length <= 15) {
              //     setValue('value', value);
              //   }
              // }}
              required
              {...register('value', {
                required: 'Watermark text is required',
                maxLength: {
                  value: 15,
                  message: 'Watermark text cannot exceed 15 characters',
                },
              })}
            ></textarea>
            <p className="flex justify-between text-slate-400 mt-1">
              <div>
                {errors.value?.message && (
                  <span className="text-red-500">{errors.value.message.toString()}</span>
                )}
              </div>
              {'(' + (watch('value')?.length ?? 0) + '/15)'}
            </p>
          </div>

          <div className="mb-5 w-full">
            <div className="font-bold text-black">Color</div>
            <div className="relative">
              <button
                className="flex justify-between items-center w-full font-bold mt-1"
                onClick={() => setShowBgColorPicker(!showBgColorPicker)}
              >
                {watch('color') ? (
                  <div className="flex ">
                    <div className="h-5 w-5 rounded-full " style={{ backgroundColor: watch('color') }}></div>
                    <p className="ml-2">{watch('color') || 'select any color'}</p>
                  </div>
                ) : (
                  'select any color'
                )}
                <ChevronRightIcon
                  className="-mr-1 ml-1 h-5 w-5 flex-shrink-0 text-gray-400 group-hover:text-gray-500"
                  aria-hidden="true"
                />
              </button>
              {showBgColorPicker && (
                <div ref={colorPickerRef} className="absolute left-5 -translate-x-[30px] top-0 z-[9999]">
                  <ColorSelector
                    currentColor={watch('color')}
                    setCurrentColor={(color) => {
                      setValue('color', color);
                      // setShowBgColorPicker(!showBgColorPicker);
                    }}
                  />
                </div>
              )}
            </div>
          </div>

          {showBackground && (
            <div className="mb-5 w-full">
              <div className="font-bold text-black">Background</div>
              <div className="relative">
                <button
                  className="flex justify-between items-center w-full font-bold mt-1"
                  onClick={() => setShowColorPicker(!showColorPicker)}
                >
                  {watch('background') ? (
                    <div className="flex ">
                      <div
                        className="h-5 w-5 rounded-full "
                        style={{ backgroundColor: watch('background') }}
                      ></div>
                      <p className="ml-2">{watch('background') || 'select any color'}</p>
                    </div>
                  ) : (
                    'select any color'
                  )}
                  <ChevronRightIcon
                    className="-mr-1 ml-1 h-5 w-5 flex-shrink-0 text-gray-400 group-hover:text-gray-500"
                    aria-hidden="true"
                  />
                </button>
                {showColorPicker && (
                  <div ref={colorPickerRef} className="absolute left-5 -translate-x-[30px] top-0">
                    <ColorSelector
                      currentColor={watch('background')}
                      setCurrentColor={(color) => {
                        setValue('background', color);
                        // setShowBgColorPicker(!showBgColorPicker);
                      }}
                    />
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="mb-5 w-full">
            <div className="font-bold text-black">Opacity</div>
            <div className="mt-2 flex items-center">
              <input
                type="range"
                id="rangeInput"
                name="rangeInput"
                min={0.1}
                max={1}
                step={0.1}
                value={watch('opacity') || 0}
                onChange={(e) => setValue('opacity', e.target.value)}
                className=" w-full"
              />
              <span className="ml-2" id="opacityValue">
                {watch('opacity') || 0}
              </span>
            </div>
          </div>
          <div className="w-full mt-4 flex md:justify-end justify-center">
            <button
              className="bg-black text-white py-2 px-4 font-bold md:w-auto rounded-full w-full flex items-center justify-center"
              onClick={handleSubmit(onSubmit)}
            >
              {isUploading ? (
                <LoadingCircleIcon classNames="w-6 h-6" />
              ) : (
                `${exisitingData ? 'Update' : 'Save'}`
              )}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default WaterMarkData;
